import React, { memo, useMemo } from 'react';
import {
  string, number, func, arrayOf, shape, object,
} from 'prop-types';
import Locale from '@active/react-utils/i18n/Locale';
import classNames from 'classnames';
import { currency } from '../../../Currency';
import AmountField from '../../../staticFields/AmountField';
import TipAmountField from '../../../staticFields/TipAmountField';

const OrderSummaryContent = (props) => {
  const {
    totalAmount, currencyCode,
    locale, currentCreditBalance, currentAmountDue,
    subTotal, discount, processingFee,
    aaDiscount, useCreditBalance,
    surchargeGroup, cancellationFee, creditFee,
    activeRefundFee, activeAdvantageFee,
    magazineTrialFee, trackingCallback,
    resetContentHeight,
    totalRefund, totalCharge,
    appliedTransferFee,
    isShowItemsContent = true,
    totalPPF,
    currentPPF,
    deposit,
    pennyRoundingAmount = 0,
    grandTotal,
  } = props;

  const values = { 0: currency({ amount: Math.abs(totalAmount), code: currencyCode, locale }) };
  const isShowLineSection = useMemo(() => currentAmountDue !== totalAmount, [ currentAmountDue, totalAmount ]);
  const isShowCurrentPPF = useMemo(() => !!currentPPF && (currentPPF !== totalPPF), [ currentPPF, totalPPF ]);
  return (
    <div className="order-summary__items">
      {
        isShowItemsContent && <div className="order-summary__items-content">
          <AmountField
            label="checkout.lib.components.orderSummary.subTotal"
            price={subTotal}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          <AmountField
            label="checkout.lib.components.text.totalRefund"
            price={totalRefund}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          <AmountField
            label="checkout.lib.components.text.totalCharge"
            price={totalCharge}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          <AmountField
            label="checkout.lib.components.text.activeRefund"
            price={activeRefundFee}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          <AmountField
            label="checkout.lib.components.text.activeAdvantage"
            price={activeAdvantageFee}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          <AmountField
            label="checkout.lib.components.text.magazineTrial"
            price={magazineTrialFee}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          <AmountField
            label="checkout.lib.components.text.discount"
            price={discount}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          {
            surchargeGroup.length > 0 && surchargeGroup.map(({ name, amount }, index) => {
              return name && <AmountField
                key={index}
                name={name}
                price={amount}
                currencyCode={currencyCode}
                className="order-summary__order-item"/>;
            })
          }
          <AmountField label="checkout.lib.components.orderDetailContent.cancellationFee"
            price={cancellationFee}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          <AmountField label="checkout.lib.components.orderDetailContent.creditFee"
            price={creditFee}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          <AmountField
            label="checkout.lib.components.text.appliedTransferFee"
            price={appliedTransferFee}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          <TipAmountField
            className="order-summary__order-item"
            label="checkout.lib.components.text.serviceFee"
            tipId="checkout.lib.components.text.serviceFee.tip"
            price={processingFee}
            currencyCode={currencyCode}
            trackingCallback={trackingCallback}
            resetContentHeight={resetContentHeight}
            fieldType={'processingFee'}
            placement="left"/>
          <AmountField
            label="checkout.lib.components.text.aaDiscount"
            price={aaDiscount}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          { !!totalPPF
            && <TipAmountField
              label="checkout.lib.components.text.totalPPF"
              tipId="checkout.lib.components.text.processingFeeCharged"
              price={totalPPF}
              fieldType={'totalPaymentProcessingFee'}
              trackingCallback={trackingCallback}
              currencyCode={currencyCode}
              className="order-summary__order-item"/>
          }
        </div>
      }
      <div className={classNames('order-summary__items-footer', {
        'pd-t-0': !isShowItemsContent,
      })} >
        <AmountField
          label="checkout.lib.components.text.orderTotal"
          price={totalAmount < 0 ? 0 : totalAmount}
          currencyCode={currencyCode}
          className="order-summary__order-amount"/>
        {
          totalAmount < 0
              && <div className="total-amount-tag">
                <Locale id='checkout.lib.components.text.willBeRefunded' params={values} />
              </div>
        }
        {isShowLineSection && <React.Fragment>
          <div className="hr-solid-line" />
          {!!deposit && <AmountField // should not show when value is 0
            label="checkout.lib.components.text.deposit"
            price={deposit}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>}
          {useCreditBalance && !!currentCreditBalance // should not show when value is 0
          && <AmountField
            label="checkout.lib.components.text.creditBalanceApplied"
            price={0 - currentCreditBalance}
            currencyCode={currencyCode}
            className="order-summary__order-item"/>
          }
          { isShowCurrentPPF
            && <AmountField
              label="checkout.lib.components.text.currentPPF"
              price={currentPPF}
              currencyCode={currencyCode}
              className="order-summary__order-item"/>
          }
          <AmountField
            label="checkout.lib.components.text.dueNow"
            price={currentAmountDue}
            currencyCode={currencyCode}
            className="order-summary__order-amount font16"/>
        </React.Fragment>}
        {
          !!pennyRoundingAmount && <div className="order-summary__penny-rounding">
            <AmountField
              label="checkout.lib.components.text.pennyRounding"
              price={pennyRoundingAmount}
              currencyCode={currencyCode}
              className="order-summary__order-item"/>
            <AmountField
              label="checkout.lib.components.text.grandTotal"
              price={grandTotal}
              currencyCode={currencyCode}
              className="order-summary__order-amount font16"/>
          </div>
        }
      </div>
    </div>
  );
};

OrderSummaryContent.propTypes = {
  totalAmount: number,
  subTotal: number,
  discount: number,
  processingFee: number,
  currencyCode: string.isRequired,
  onSubmit: func.isRequired,
  thresholdId: string,
  buttonTextId: string,
  paymentMethodType: string,
  currentAmountDue: number,
  currentCreditBalance: number,
  surchargeGroup: arrayOf(shape({
    name: string,
    amount: number,
  })),
  cancellationFee: number,
  creditFee: number,
  activeRefundFee: number,
  activeAdvantageFee: number,
  locale: string,
  orderSummaryItemsFooterRef: object,
  orderSummaryItemsContentRef: object,
};

OrderSummaryContent.defaultProps = {
  surchargeGroup: [],
};

export default memo(OrderSummaryContent);
